/* eslint-disable ember/classic-decorator-no-classic-methods, ember/no-get, ember/no-runloop, ember/require-computed-property-dependencies, no-restricted-properties, no-unused-vars, warp-drive/no-legacy-request-patterns */
import { tracked } from '@glimmer/tracking';
import Controller from '@ember/controller';
import EmberObject, { action, computed, set } from '@ember/object';
import { TrackedArray } from 'tracked-built-ins';
import { service } from '@ember/service';
import DataType from '@linc-technologies/linced-common/enums/data-type';
import Jurisdiction from '@linc-technologies/linced-common/enums/jurisdiction';
import ReportTemplateSectionType from '@linc-technologies/linced-common/enums/reports/report-template-section-type';
import ReportTemplateTheme from '@linc-technologies/linced-common/enums/reports/report-template-theme';
import ReportStudentNameFormat from '@linc-technologies/linced-common/enums/reports/report-student-name-format';
import { resolveNameFormat } from '@linc-technologies/linced-common/utils/report-student-name';
import ReportTemplateType from '@linc-technologies/linced-common/enums/reports/report-template-type';
import StudentReportState from '@linc-technologies/linced-common/enums/reports/student-report-state';
import {
  byKeys,
  filterBy,
  findBy,
  isAny,
} from '@linc-technologies/linced-common/utils/arrays';
import {
  rubricFamilyIdOf,
  rubricFamilyOrDefault,
  sortRubricFamilies,
} from '@linc-technologies/linced-common/utils/rubrics';
import { defaultTinyMCEConfig } from '@linc-technologies/linced-common/utils/tinymce';
import { scheduleOnce } from '@ember/runloop';
import {
  ASSESSMENT_DISPLAY_OPTIONS,
  ASSESSMENT_GRADE_SCOPE_OPTIONS,
  ATTENDANCE_DISPLAY_OPTIONS,
  STRAND_VISUAL_OPTIONS,
  PROGRESS_CHART_OPTIONS,
} from 'linced-app/utils/chart-type-options';
import { resolveIds } from 'linced-app/utils/reporting';
import { DEFAULT_JUDGEMENT_WORDING } from 'linced-app/utils/rubric-comments';

const DEFAULT_JUDGEMENT_WORDING_KEYS = Object.keys(DEFAULT_JUDGEMENT_WORDING);

// Days selectable per month, indexed by the 1-based month number. February is
// capped at 28 rather than 29: a period repeats every year, so a 29th would be
// an invalid date in three years out of four.
const DAYS_IN_MONTH = Object.freeze({
  1: 31,
  2: 28,
  3: 31,
  4: 30,
  5: 31,
  6: 30,
  7: 31,
  8: 31,
  9: 30,
  10: 31,
  11: 30,
  12: 31,
});

const MAX_DAYS_IN_MONTH = 31;

function daysInMonth(month) {
  return DAYS_IN_MONTH[month] || MAX_DAYS_IN_MONTH;
}

export default class ReportBuilderController extends Controller {
  @action
  setModulePickerSearch(value) {
    this.modulePickerSearch = value;
  }

  queryParams = ['type'];
  type = 'report';
  editorOptions = Object.freeze(defaultTinyMCEConfig());
  ASSESSMENT_DISPLAY_OPTIONS = ASSESSMENT_DISPLAY_OPTIONS;
  ASSESSMENT_GRADE_SCOPE_OPTIONS = ASSESSMENT_GRADE_SCOPE_OPTIONS;
  ATTENDANCE_DISPLAY_OPTIONS = ATTENDANCE_DISPLAY_OPTIONS;
  STRAND_VISUAL_OPTIONS = STRAND_VISUAL_OPTIONS;
  PROGRESS_CHART_OPTIONS = PROGRESS_CHART_OPTIONS;

  // Services
  @service
  fetch;

  @service
  router;

  @service
  strings;

  @service
  session;

  @service
  store;

  @service
  toaster;

  @service('school-schema')
  schoolSchema;

  themeOptions = Object.freeze(['Teal', 'Blue', 'Warm', 'Monochrome']);

  @computed('template.theme')
  get selectedTheme() {
    let themeValue = this.get('template.theme');
    let entry = ReportTemplateTheme.values.find((v) => v.value === themeValue);
    return entry && entry.label !== 'Unspecified' ? entry.label : 'Teal';
  }

  @tracked
  template = null;
  @tracked _savedSnapshot = null;
  @tracked _dirtyCounter = 0;

  // Set by the copy flow so a freshly-copied template skips the baseline
  // snapshot and reads as dirty (Save enabled), like create-from-scratch.
  @tracked
  freshCopy = false;

  // True while the loaded template is an unsaved copy — persisted server-side
  // (so isNew is false) but never saved by this user. Drives the first-save
  // activate prompt. Re-derived from freshCopy on every load in setupForEdit.
  @tracked
  unsavedCopy = false;

  get isDirty() {
    if (!this._savedSnapshot) return true;
    return this._getCurrentSnapshot() !== this._savedSnapshot;
  }

  @tracked selectedSection = null;
  @tracked
  selectedComponent = null;

  // The three drill-in states each replace the whole form pane. Stacked on a
  // small screen that leaves the pane below the preview, so the template flags
  // them and the stylesheet lifts the pane into a bottom sheet instead.
  get isDetailView() {
    return (
      this.showDataTriangulation ||
      this.selectedComponent ||
      this.selectedSection
    );
  }

  @tracked
  showSectionTypes = false;
  @tracked
  showComponentPicker = false;
  @tracked
  showTemplateAttributes = false;
  @tracked
  showSaveConfirmation = false;
  @tracked
  saveConfirmationCounts = null;
  @tracked
  showActivatePrompt = false;
  @tracked
  saveConfirmationDeactivating = false;

  // Student reports reference their period by position (`periodIndex`), not by
  // id, so once reports exist against a template version, re-dating a period or
  // adding/removing one silently re-points those reports at a different period.
  // Labels stay editable — they're display-only. Void reports are discarded and
  // don't pin the structure.
  @tracked
  periodsLocked = false;
  @tracked
  isCheckingPeriodLock = false;
  @tracked
  periodLockCheckFailed = false;
  @tracked
  existingReportCount = 0;

  // Keep the period controls disabled while the check is in flight and if it
  // fails: an unverified template must never read as editable.
  get periodsEditingDisabled() {
    return (
      this.periodsLocked ||
      this.isCheckingPeriodLock ||
      this.periodLockCheckFailed
    );
  }

  @computed('saveConfirmationCounts.@each.affected')
  get hasAffectedSaveCounts() {
    return isAny(this.saveConfirmationCounts || [], 'affected');
  }

  monthOptions = Object.freeze([
    { value: 1, label: 'January' },
    { value: 2, label: 'February' },
    { value: 3, label: 'March' },
    { value: 4, label: 'April' },
    { value: 5, label: 'May' },
    { value: 6, label: 'June' },
    { value: 7, label: 'July' },
    { value: 8, label: 'August' },
    { value: 9, label: 'September' },
    { value: 10, label: 'October' },
    { value: 11, label: 'November' },
    { value: 12, label: 'December' },
  ]);

  @computed
  get monthOptionsMap() {
    let map = {};
    this.monthOptions.forEach((m) => {
      map[m.value] = m;
    });
    return map;
  }

  // Every day 1-31. Each month's select gets a slice of this one array, so the
  // option a `dayOptionsMap` lookup returns is identity-equal to the option in
  // whichever month's list is being rendered — which is what PaperSelect
  // matches `@selected` on.
  @computed
  get dayOptions() {
    let days = [];
    for (let i = 1; i <= MAX_DAYS_IN_MONTH; i++) {
      days.push({ value: i, label: String(i) });
    }
    return Object.freeze(days);
  }

  @computed
  get dayOptionsByMonth() {
    let map = {};
    this.monthOptions.forEach((m) => {
      map[m.value] = Object.freeze(
        this.dayOptions.slice(0, daysInMonth(m.value)),
      );
    });
    return map;
  }

  @computed
  get dayOptionsMap() {
    let map = {};
    this.dayOptions.forEach((d) => {
      map[d.value] = d;
    });
    return map;
  }

  headerComponentOptions = Object.freeze([
    {
      group: 'CONTENT',
      items: [
        {
          label: 'Paragraph',
          description: 'Static text – always available',
          type: 'paragraph',
          icon: 'text_fields',
          hasSettings: true,
        },
      ],
    },
    {
      group: 'STRUCTURE',
      items: [
        {
          label: 'School name and logo',
          description: 'School logo with name',
          type: 'school-name-logo',
          icon: 'school',
        },
        {
          label: 'Student name and avatar',
          description: 'Student photo with name (mandatory, always shown)',
          type: 'student-name-avatar',
          icon: 'person',
          hasSettings: true,
        },
        {
          label: 'Student detail',
          description: 'DOB, class, year level and custom fields',
          type: 'student-detail',
          icon: 'badge',
          hasSettings: true,
        },
      ],
    },
  ]);
  _allSectionTypeOptions = Object.freeze([
    {
      label: 'Header',
      description: 'Student identity (name, avatar, DOB)',
      sectionType: ReportTemplateSectionType.HEADER,
      icon: 'view_headline',
    },
    {
      label: 'Content',
      description:
        'Subject area with layout (add Assessment Data as a component)',
      sectionType: ReportTemplateSectionType.CONTENT,
      icon: 'article',
    },
    // {
    //   label: 'Detail',
    //   description: 'Plan details (staff, goals)',
    //   sectionType: ReportTemplateSectionType.DETAIL,
    //   icon: 'info',
    // },
    {
      label: 'Attendance',
      description: 'Attendance summary',
      sectionType: ReportTemplateSectionType.ATTENDANCE,
      icon: 'event_available',
    },
    {
      label: 'Information page',
      description: 'Static text only (paragraphs)',
      sectionType: ReportTemplateSectionType.INFORMATION_PAGE,
      icon: 'description',
    },
    {
      label: 'Leadership Comment',
      description:
        'Leadership comment section (not in ReportEditor, does not count towards completion)',
      sectionType: ReportTemplateSectionType.PRINCIPAL_COMMENT,
      icon: 'record_voice_over',
    },
  ]);

  // Plain getter (not @computed): `sections` is a TrackedArray mutated
  // in place (push/splice) by addSectionOfType/removeSection/sortSections,
  // and classic @computed's `@each` dependent key doesn't observe
  // TrackedArray mutations or per-item property reads the way it does for
  // EmberArray — only autotracking does.
  get sectionTypeOptions() {
    let existingSectionTypes = new Set(
      (this.sections || []).map((s) => s.get('sectionType')),
    );
    let singletonTypes = [
      ReportTemplateSectionType.HEADER,
      ReportTemplateSectionType.ATTENDANCE,
      ReportTemplateSectionType.PRINCIPAL_COMMENT,
    ];
    return this._allSectionTypeOptions
      .filter(
        (option) =>
          !(
            singletonTypes.includes(option.sectionType) &&
            existingSectionTypes.has(option.sectionType)
          ),
      )
      .sort(byKeys('label'));
  }

  setupForNew(templateType) {
    let isPlan = templateType === 'plan';
    let template = this.store.createRecord('reports/report-template', {
      name: isPlan ? 'New Plan' : 'New Report',
      description: '',
      type: isPlan ? ReportTemplateType.PLAN : ReportTemplateType.REPORT,
      isActive: false,
      schoolId: this.session.school,
      theme: ReportTemplateTheme.TEAL,
      studentFirstNameFormat: ReportStudentNameFormat.PREFERRED,
      studentLastNameFormat: ReportStudentNameFormat.PREFERRED,
    });

    template.get('periods').createFragment({
      label: 'Mid Year',
      month: 6,
      day: 28,
    });

    template.get('periods').createFragment({
      label: 'End of Year',
      month: 12,
      day: 28,
    });

    this.template = template;
    this._deletedSections = [];
    this._ensureSampleStudent();
    this._clearViewSelection();
    this._resetSelectionState();
    this._savedSnapshot = null;
    this.showTemplateAttributes = true;

    // Create a default Header section
    let studentNameComponent = EmberObject.create({
      label: 'Student name and avatar',
      type: 'student-name-avatar',
      icon: 'person',
      locked: true,
      hasSettings: true,
      includeClass: true,
      includeYearLevel: true,
      includeClassTeachers: false,
      classFieldKey: 'learner:homeroom',
      yearLevelFieldKey: 'core:currentYearLevel',
    });
    let headerSection = this.store.createRecord(
      'reports/report-template-section',
      {
        reportTemplateId: template,
        label: 'Header',
        icon: 'view_headline',
        sectionType: ReportTemplateSectionType.HEADER,
        sequence: 0,
        schoolId: this.session.school,
        configuration: {
          // TrackedArray, not a plain array: sortComponents mutates this in
          // place with native splice via the section-components DragSortList.
          components: new TrackedArray([
            {
              label: 'School name and logo',
              type: 'school-name-logo',
              icon: 'school',
              locked: true,
            },
            studentNameComponent,
          ]),
        },
      },
    );
    this.studentNameSettings = studentNameComponent;
    // TrackedArray, not a plain array: sortSections mutates this in place
    // with native splice via the report-sections DragSortList.
    this.sections = new TrackedArray([headerSection]);
    this._setupBeforeUnload();

    // A template that doesn't exist yet can't have reports against it.
    this._checkPeriodLock(template);
  }

  setupForEdit(template, sections) {
    this.template = template;
    this._ensureSampleStudent();

    let sorted = sections.slice().sort((a, b) => a.sequence - b.sequence);
    let liveSections = new TrackedArray();

    sorted.forEach((section) => {
      let raw = section.get('configuration');
      let liveConfig = this._deserializeConfiguration(raw);
      section.set('configuration', liveConfig);

      // Wire up studentNameSettings / studentDetailSettings from Header components
      if (section.get('sectionType') === ReportTemplateSectionType.HEADER) {
        let comps = liveConfig.components || [];
        comps.forEach((comp) => {
          if (comp.get('type') === 'student-name-avatar') {
            this.studentNameSettings = comp;
          }
          if (comp.get('type') === 'student-detail') {
            this.studentDetailSettings = comp;
          }
        });
      }

      liveSections.push(section);
    });

    this.sections = liveSections;
    this._deletedSections = [];
    this._resetSelectionState();

    // Restore view selection after a save-triggered reload
    if (this._pendingSelectionRestore) {
      let { sectionSequence, componentIndex } = this._pendingSelectionRestore;
      this._pendingSelectionRestore = null;
      if (sectionSequence != null) {
        let section = findBy(liveSections, 'sequence', sectionSequence);
        if (section) {
          this.selectedSection = section;
          if (componentIndex != null && componentIndex >= 0) {
            let comp = section.get('configuration').components[componentIndex];
            if (comp) {
              this.selectedComponent = comp;
            }
          }
        }
      }
    }

    let hasContentSection = liveSections.some(
      (s) => s.get('sectionType') === ReportTemplateSectionType.CONTENT,
    );
    if (hasContentSection) {
      this._loadSubjects();
    }

    // Snapshot the initial state for dirty checking. A freshly-copied template
    // skips this so it reads as dirty (Save enabled) without a token edit, and
    // is flagged so its first Save still offers the activate prompt.
    this.unsavedCopy = this.freshCopy;
    this._savedSnapshot = this.freshCopy ? null : this._takeSnapshot();
    this.freshCopy = false;
    this._setupBeforeUnload();

    // Not awaited: the builder renders straight away and the periods section
    // locks itself once the counts land (it starts disabled, so there's no
    // window in which it reads as editable).
    this._checkPeriodLock(template);
  }

  // Counts the student reports already written against this template version,
  // which decides whether the periods section is editable.
  //
  // Lists the template's student reports with consolidateTemplateVersions, so
  // reports written against an earlier version of the template still lock the
  // version being edited. The reporting hub's counts aren't used: they drop
  // finished past periods and retired templates' published reports, which
  // still hold the period structure.
  //
  // Void and Not Applicable reports are excluded: they're discarded and don't
  // hold the period structure.
  async _checkPeriodLock(template) {
    if (!template || template.isNew) {
      this.periodsLocked = false;
      this.isCheckingPeriodLock = false;
      this.periodLockCheckFailed = false;
      this.existingReportCount = 0;
      return;
    }

    let templateId = template.id;
    this.isCheckingPeriodLock = true;
    this.periodLockCheckFailed = false;

    try {
      let reports = await this.store.query('reports/student-report', {
        schoolId: this.session.activeSchool,
        reportTemplateId: templateId,
        consolidateTemplateVersions: true,
      });

      // The builder may have moved on to another template (or been torn down)
      // while this was in flight — don't apply a stale result.
      if (!this._isStillEditing(templateId)) return;

      let existingReportCount = reports.filter(
        (report) =>
          report.state !== StudentReportState.VOID &&
          report.state !== StudentReportState.NOT_APPLICABLE,
      ).length;

      this.existingReportCount = existingReportCount;
      this.periodsLocked = existingReportCount > 0;
    } catch (e) {
      console.error('Failed to check reports against this template:', e);
      if (!this._isStillEditing(templateId)) return;
      // Fail closed: if we can't tell whether reports exist, leave the period
      // controls disabled rather than risk re-pointing existing reports.
      this.periodLockCheckFailed = true;
      this.existingReportCount = 0;
    } finally {
      if (this._isStillEditing(templateId)) {
        this.isCheckingPeriodLock = false;
      }
    }
  }

  _isStillEditing(templateId) {
    let current = this.template;
    return !!current && !current.isDestroyed && current.id === templateId;
  }

  cleanup() {
    // Rollback unsaved changes on template and sections
    let template = this.template;
    if (template && !template.get('isDestroyed')) {
      if (template.get('isNew')) {
        template.deleteRecord();
      } else if (template.get('hasDirtyAttributes')) {
        template.rollbackAttributes();
      }
    }

    (this.sections || []).forEach((section) => {
      if (section && !section.get('isDestroyed')) {
        if (section.get('isNew')) {
          section.deleteRecord();
        } else if (section.get('hasDirtyAttributes')) {
          section.rollbackAttributes();
        }
      }
    });

    // Rollback locally-deleted existing sections (removeSection flagged
    // them isDeleted to hide from the preview) so they reappear if the
    // user navigates away without saving.
    (this._deletedSections || []).forEach((section) => {
      if (section && !section.get('isDestroyed') && section.get('isDeleted')) {
        section.rollbackAttributes();
      }
    });

    this.template = null;
    this.sections = null;
    this._deletedSections = null;
    this._clearViewSelection();
    this._teardownBeforeUnload();
    this._resetUIState();
  }

  _markDirty() {
    this.incrementProperty('_dirtyCounter');
  }

  _resetSelectionState() {
    this.showSectionTypes = false;
    this.showComponentPicker = false;
    this.showTemplateAttributes = false;
    this.showDataTriangulation = false;
    this.studentNameSettings = null;
    this.studentDetailSettings = null;
    this.studentFieldOptions = null;
  }

  _clearViewSelection() {
    this.selectedSection = null;
    this.selectedComponent = null;
  }

  _scrollToPeriods() {
    let el = document.getElementById('report-builder-periods');
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }

  _beforeUnloadHandler = null;

  _setupBeforeUnload() {
    this._teardownBeforeUnload();
    this._beforeUnloadHandler = (event) => {
      if (this.isSaving || this.isDirty) {
        event.preventDefault();
        event.returnValue = '';
      }
    };
    window.addEventListener('beforeunload', this._beforeUnloadHandler);
  }

  _teardownBeforeUnload() {
    if (this._beforeUnloadHandler) {
      window.removeEventListener('beforeunload', this._beforeUnloadHandler);
      this._beforeUnloadHandler = null;
    }
  }

  _resetUIState() {
    this._resetSelectionState();
    this.sampleStudent = null;
    // Back to "locked until proven otherwise" for the next template loaded.
    this.periodsLocked = false;
    this.isCheckingPeriodLock = false;
    this.periodLockCheckFailed = false;
    this.existingReportCount = 0;
  }

  _deserializeConfiguration(raw) {
    let parsed = raw;
    if (typeof raw === 'string') {
      try {
        parsed = JSON.parse(raw);
      } catch (e) {
        parsed = {};
      }
    }
    if (!parsed || typeof parsed !== 'object') {
      parsed = {};
    }

    let result = {};
    if (parsed.subjectId != null) {
      result.subjectId = parsed.subjectId;
    }
    if (parsed.rubricFamilyId != null) {
      result.rubricFamilyId = parsed.rubricFamilyId;
    }
    // Plain arrays: mutated via removeObject-turned-splice plus an explicit
    // notifyPropertyChange('configuration') call in the removeTriangulationX
    // actions, which forces the classic @computed dependent keys watching
    // 'selectedSection.configuration.triangulationXIds.[]' to re-resolve
    // regardless of the underlying array type - no TrackedArray needed.
    if (parsed.triangulationSubjectIds) {
      result.triangulationSubjectIds = [...parsed.triangulationSubjectIds];
    }
    if (parsed.triangulationPageIds) {
      result.triangulationPageIds = [...parsed.triangulationPageIds];
    }
    if (parsed.triangulationAssessmentIds) {
      result.triangulationAssessmentIds = [
        ...parsed.triangulationAssessmentIds,
      ];
    }
    // TrackedArray, not a plain array: sortComponents mutates this in place
    // with native splice via the section-components DragSortList.
    result.components = new TrackedArray(
      (parsed.components || []).map((plain) =>
        this._deserializeComponent(plain),
      ),
    );
    return result;
  }

  _deserializeComponent(plain) {
    let keys = [
      'label',
      'type',
      'icon',
      'hasSettings',
      'locked',
      'content',
      'customLabel',
      'narrativeGroup',
      'firstNameKey',
      'lastNameKey',
      'includeClass',
      'includeYearLevel',
      'includeClassTeachers',
      'classFieldKey',
      'yearLevelFieldKey',
      'chartType',
      'visualType',
      'chipLabel',
      'useSchoolColour',
      'displayType',
      'gradeScope',
    ];
    let props = {};
    keys.forEach((key) => {
      if (plain[key] !== undefined) {
        props[key] = plain[key];
      }
    });

    // Keep selectedFields as plain IDs. TrackedArray, not a plain array:
    // sortDetailFields mutates this in place with native splice via the
    // detail-fields DragSortList.
    if (plain.selectedFields) {
      props.selectedFields = new TrackedArray(plain.selectedFields);
    }

    // Deserialize modules: resolve IDs to { id, label } from the store.
    // Plain array: modules is only ever replaced wholesale (confirmModule
    // Selection) or filtered-and-reassigned (removeAssessmentModule), never
    // mutated in place, so no TrackedArray needed.
    if (plain.modules) {
      props.modules = plain.modules
        .map((m) => {
          let id = typeof m === 'string' ? m : m.id;
          let record = this.store.peekRecord('assessments/module', id);
          return record
            ? { id: record.get('id'), label: record.get('label') }
            : null;
        })
        .filter(Boolean);
    }

    return EmberObject.create(props);
  }

  @tracked
  _deletedSections = null;

  @computed('template.name')
  get templateName() {
    return this.get('template.name') || 'New Report';
  }

  nameFormatOptions = Object.freeze(['Preferred', 'Legal']);

  // The four phrases a template can set for the bold opening line of
  // generated teacher comments, each showing the default it replaces
  judgementWordingFields = Object.freeze([
    {
      key: 'interimOnTrack',
      label: 'Mid-year, on track',
      placeholder: DEFAULT_JUDGEMENT_WORDING.interimOnTrack,
    },
    {
      key: 'interimNotOnTrack',
      label: 'Mid-year, not on track',
      placeholder: DEFAULT_JUDGEMENT_WORDING.interimNotOnTrack,
    },
    {
      key: 'finalMet',
      label: 'End of year, met',
      placeholder: DEFAULT_JUDGEMENT_WORDING.finalMet,
    },
    {
      key: 'finalNotMet',
      label: 'End of year, not met',
      placeholder: DEFAULT_JUDGEMENT_WORDING.finalNotMet,
    },
  ]);

  // What each judgement wording box shows: the template's phrase, or the
  // default it replaces so schools can see and edit the current wording.
  // Only recomputed when the template changes, so a box the teacher clears
  // stays empty rather than refilling with the default while they type.
  @computed('template')
  get judgementWordingValues() {
    let wording = this.template?.judgementWording;
    return Object.fromEntries(
      DEFAULT_JUDGEMENT_WORDING_KEYS.map((key) => [
        key,
        wording?.[key] || DEFAULT_JUDGEMENT_WORDING[key],
      ]),
    );
  }

  // Which overall judgements count as on track, in the school's terms:
  // mid-year from "at" (3), end of year from "above" (4).
  @computed('strings.{progressAt,progressAbove}')
  get judgementWordingThresholds() {
    let { progressAt: at, progressAbove: above } = this.strings;
    return `On track mid-year means ${at} and above; met at the end of the year means ${above} and above.`;
  }

  @computed('selectedSection.sectionType')
  get isHeaderSection() {
    return (
      this.selectedSection &&
      this.selectedSection.get('sectionType') ===
        ReportTemplateSectionType.HEADER
    );
  }

  @computed('selectedSection.sectionType')
  get isContentSection() {
    return (
      this.selectedSection &&
      this.selectedSection.get('sectionType') ===
        ReportTemplateSectionType.CONTENT
    );
  }

  @computed('selectedSection.sectionType')
  get isDetailSection() {
    return (
      this.selectedSection &&
      this.selectedSection.get('sectionType') ===
        ReportTemplateSectionType.DETAIL
    );
  }

  @computed('selectedSection.sectionType')
  get isAttendanceSection() {
    return (
      this.selectedSection &&
      this.selectedSection.get('sectionType') ===
        ReportTemplateSectionType.ATTENDANCE
    );
  }

  @computed('selectedSection.sectionType')
  get isInformationPageSection() {
    return (
      this.selectedSection &&
      this.selectedSection.get('sectionType') ===
        ReportTemplateSectionType.INFORMATION_PAGE
    );
  }

  @computed('selectedSection.sectionType')
  get isPrincipalCommentSection() {
    return (
      this.selectedSection &&
      this.selectedSection.get('sectionType') ===
        ReportTemplateSectionType.PRINCIPAL_COMMENT
    );
  }

  @tracked
  subjectOptions = null;

  // Subjects selectable as a content section's main subject. Trackable-only —
  // non-trackable subjects (narrative-only, taxonomy-only, etc.) have no
  // possibleGrades, so the progress-descriptor chip and strand-visual can't
  // resolve a label / row from them. Triangulation reads the broader
  // subjectOptions list directly because non-trackable subjects are valid
  // sources for a triangulation reference (HERO-12575: 9f01b60de widened the
  // shared list for triangulation; this computed restores the trackable-only
  // filter just for the main-subject dropdown).
  //
  // Grouped by subjectTypeName for the PaperSelect heading layout — matches
  // the triangulation picker. subjectOptions is already sorted by
  // subjectTypePriority then label, so Map insertion order gives groups in
  // priority order and options within each group stay alphabetical.
  @computed('subjectOptions.[]')
  get mainSubjectOptions() {
    let groups = new Map();
    filterBy(this.subjectOptions || [], 'trackable').forEach((subject) => {
      let name = subject.get('subjectTypeName') || 'Other';
      if (!groups.has(name)) {
        groups.set(name, { groupName: name, options: [] });
      }
      groups.get(name).options.push(subject);
    });
    return Array.from(groups.values());
  }

  // Data triangulation picker state — Subjects
  @tracked
  showTriangulationSubjectPicker = false;

  @tracked
  pendingTriangulationSubjects = null;

  @computed(
    'selectedSection.configuration.triangulationSubjectIds.[]',
    'subjectOptions.[]',
  )
  get triangulationSubjects() {
    let ids =
      this.get('selectedSection.configuration.triangulationSubjectIds') || [];
    return resolveIds(ids, this.subjectOptions);
  }

  // Data triangulation picker state — Pages
  @tracked
  pageOptions = null;

  @tracked
  showTriangulationPagePicker = false;
  @tracked
  pendingTriangulationPages = null;

  @computed(
    'selectedSection.configuration.triangulationPageIds.[]',
    'pageOptions.[]',
  )
  get triangulationPages() {
    let ids =
      this.get('selectedSection.configuration.triangulationPageIds') || [];
    return resolveIds(ids, this.pageOptions);
  }

  // Data triangulation picker state — Assessments
  @tracked
  assessmentOptions = null;

  @tracked
  showTriangulationAssessmentPicker = false;
  @tracked
  pendingTriangulationAssessments = null;

  @computed(
    'selectedSection.configuration.triangulationAssessmentIds.[]',
    'assessmentOptions.[]',
  )
  get triangulationAssessments() {
    let ids =
      this.get('selectedSection.configuration.triangulationAssessmentIds') ||
      [];
    return resolveIds(ids, this.assessmentOptions);
  }

  @computed(
    'selectedSection.configuration.{triangulationSubjectIds.[],triangulationPageIds.[],triangulationAssessmentIds.[]}',
  )
  get triangulationConfigured() {
    let subjectIds =
      this.get('selectedSection.configuration.triangulationSubjectIds') || [];
    let pageIds =
      this.get('selectedSection.configuration.triangulationPageIds') || [];
    let assessmentIds =
      this.get('selectedSection.configuration.triangulationAssessmentIds') ||
      [];
    return (
      subjectIds.length > 0 || pageIds.length > 0 || assessmentIds.length > 0
    );
  }

  @computed(
    'selectedSection.configuration.{triangulationSubjectIds.[],triangulationPageIds.[],triangulationAssessmentIds.[]}',
  )
  get triangulationSummary() {
    let subjectCount = (
      this.get('selectedSection.configuration.triangulationSubjectIds') || []
    ).length;
    let pageCount = (
      this.get('selectedSection.configuration.triangulationPageIds') || []
    ).length;
    let assessmentCount = (
      this.get('selectedSection.configuration.triangulationAssessmentIds') || []
    ).length;
    let parts = [];
    if (subjectCount) {
      parts.push(
        `${subjectCount} ${subjectCount === 1 ? 'subject' : 'subjects'}`,
      );
    }
    if (pageCount) {
      parts.push(`${pageCount} ${pageCount === 1 ? 'page' : 'pages'}`);
    }
    if (assessmentCount) {
      parts.push(
        `${assessmentCount} ${
          assessmentCount === 1 ? 'assessment' : 'assessments'
        }`,
      );
    }
    return parts.join(', ');
  }

  // Rubric families (and the rubrics in them) for the school's jurisdiction,
  // so a content section can choose which family's rubric it uses.
  _loadRubricFamilies() {
    if (this._rubricFamiliesLoading) return;
    let jurisdiction = this.session.school?.jurisdiction;
    if (!jurisdiction) return;
    this._rubricFamiliesLoading = Promise.all([
      this.store.query('reports/rubric-family', { jurisdiction }),
      this.store.query('reports/rubric', {
        isGlobal: true,
        isActive: true,
        jurisdiction,
      }),
    ])
      .then(([families, rubrics]) => {
        if (this.isDestroying || this.isDestroyed) return;
        this.rubricFamilyOptions = [...families];
        this.rubricOptions = [...rubrics];
      })
      .catch(() => {
        this._rubricFamiliesLoading = null;
        this.toaster.error('Failed to load rubrics.');
      });
  }

  _loadSubjects() {
    this._loadRubricFamilies();
    if (this.subjectOptions) return;
    this.store
      .findAll('goals/subject')
      .then((subjects) => {
        this.subjectOptions = subjects
          .filter((s) => s.get('visibleToTeachers'))
          .sort((a, b) =>
            a.subjectTypePriority !== b.subjectTypePriority
              ? a.subjectTypePriority > b.subjectTypePriority
                ? 1
                : -1
              : a.label > b.label
                ? 1
                : a.label < b.label
                  ? -1
                  : 0,
          );
      })
      .catch(() => {
        this.toaster.error('Failed to load subjects.');
      });
  }

  _loadPages() {
    if (this.pageOptions) return;
    this.store
      .findAll('posts/page')
      .then((pages) => {
        this.pageOptions = filterBy(pages, 'visibleInMenu', true).sort(
          byKeys('label'),
        );
      })
      .catch(() => {
        this.toaster.error('Failed to load pages.');
      });
  }

  _loadAssessments() {
    if (this.assessmentOptions) return;
    this.store
      .findAll('assessments/assessment')
      .then((assessments) => {
        this.assessmentOptions = assessments
          .slice()
          .sort((a, b) => (a.label || '').localeCompare(b.label || ''));
      })
      .catch(() => {
        this.toaster.error('Failed to load assessments.');
      });
  }

  _loadModules() {
    if (this.moduleOptions) return;
    this.store
      .findAll('assessments/module')
      .then((modules) => {
        this.moduleOptions = modules;
      })
      .catch(() => {
        this.toaster.error('Failed to load modules.');
      });
  }

  @tracked
  moduleOptions = null;
  @tracked
  showModulePicker = false;
  @tracked modulePickerSearch = '';
  @tracked
  pendingModuleSelections = null;

  @computed('assessmentOptions.[]', 'moduleOptions.[]', 'modulePickerSearch')
  get groupedModuleOptions() {
    let assessments = this.assessmentOptions;
    let modules = this.moduleOptions;
    if (!assessments || !modules) return [];
    let search = (this.modulePickerSearch || '').toLowerCase().trim();
    let groups = [];
    assessments.forEach((assessment) => {
      if (
        search &&
        !(assessment.get('label') || '').toLowerCase().includes(search)
      ) {
        return;
      }
      let assessmentModules = filterBy(
        modules,
        'assessmentId.id',
        assessment.get('id'),
      ).sort(byKeys('sequence', 'label'));
      if (assessmentModules.length) {
        groups.push({
          label: assessment.get('label'),
          modules: assessmentModules,
        });
      }
    });
    return groups;
  }

  @computed('pendingModuleSelections.[]')
  get pendingModuleCount() {
    return this.pendingModuleSelections
      ? this.pendingModuleSelections.length
      : 0;
  }

  @computed('selectedSectionSubject')
  get contentComponentOptions() {
    let groups = [
      {
        group: 'STATIC CONTENT',
        items: [
          {
            label: 'Paragraph',
            description: 'Static text – always available',
            type: 'paragraph',
            icon: 'text_fields',
            hasSettings: true,
          },
        ],
      },
      {
        group: 'TEXT BASED ELEMENTS',
        items: [
          {
            label: 'Teacher comment',
            description: 'Teacher narrative / comment block',
            type: 'teacher-comment',
            icon: 'comment',
            hasSettings: true,
            narrativeGroup: true,
          },
          {
            label: 'Next steps',
            description: 'Goals or next steps for the student',
            type: 'next-steps',
            icon: 'arrow_forward',
            hasSettings: true,
            narrativeGroup: true,
          },
          {
            label: 'Help at home',
            description: 'How parents can support at home',
            type: 'help-at-home',
            icon: 'home',
            hasSettings: true,
            narrativeGroup: true,
          },
        ],
      },
    ];
    if (this.selectedSectionSubject) {
      groups.push({
        group: 'DESCRIPTORS',
        items: [
          {
            label: 'Effort',
            description: 'Overall subject effort level',
            type: 'effort',
            icon: 'fitness_center',
            hasSettings: true,
            defaultChipLabel: 'Effort:',
            defaultUseSchoolColour: true,
          },
          {
            label: 'Progress descriptor',
            description: 'Overall subject grade / progress label',
            type: 'progress-descriptor',
            icon: 'grade',
            hasSettings: true,
            defaultChipLabel: 'Progress Descriptor:',
            defaultUseSchoolColour: true,
          },
        ],
      });
      groups.push({
        group: 'VISUALS',
        items: [
          {
            label: 'Progress Visual',
            description: 'Subject progress as table or chart',
            type: 'progress-visual',
            icon: 'bar_chart',
            hasSettings: true,
            defaultChartType: 'table',
          },
          {
            label: 'Strand/Element Visual',
            description: 'Subject strands as slider or table',
            type: 'strand-visual',
            icon: 'view_list',
            hasSettings: true,
            defaultVisualType: 'slider',
          },
        ],
      });
    }
    groups.push({
      group: 'ASSESSMENTS',
      items: [
        {
          label: 'Assessment Data',
          description: 'Assessment grades as table or card view',
          type: 'assessment-data',
          icon: 'assessment',
          hasSettings: true,
        },
      ],
    });
    return groups;
  }

  detailComponentOptions = Object.freeze([
    {
      group: 'ASSESSMENTS',
      items: [
        {
          label: 'Assessment Data',
          description: 'Assessment grades as table or card view',
          type: 'assessment-data',
          icon: 'assessment',
          hasSettings: true,
        },
      ],
    },
    {
      group: 'CONTENT',
      items: [
        {
          label: 'Paragraph',
          description: 'Static text – always available',
          type: 'paragraph',
          icon: 'text_fields',
          hasSettings: true,
        },
      ],
    },
  ]);
  attendanceComponentOptions = Object.freeze([
    {
      group: 'ATTENDANCE',
      items: [
        {
          label: 'Attendance Data',
          description: 'Attendance summary as chart or table',
          type: 'attendance-data',
          icon: 'event_available',
          hasSettings: true,
        },
      ],
    },
  ]);
  informationPageComponentOptions = Object.freeze([
    {
      group: 'CONTENT',
      items: [
        {
          label: 'Paragraph',
          description: 'Static text – always available',
          type: 'paragraph',
          icon: 'text_fields',
          hasSettings: true,
        },
      ],
    },
  ]);
  principalCommentComponentOptions = Object.freeze([
    {
      group: 'CONTENT',
      items: [
        {
          label: 'Paragraph',
          description: 'Static text – always available',
          type: 'paragraph',
          icon: 'text_fields',
          hasSettings: true,
        },
      ],
    },
    {
      group: 'NARRATIVE',
      items: [
        {
          label: 'Comment',
          description: 'Leadership comment text block',
          type: 'principal-comment',
          icon: 'comment',
          hasSettings: true,
          narrativeGroup: true,
        },
      ],
    },
  ]);

  // Plain getter (not @computed): selectedSection.configuration.components
  // is a TrackedArray mutated in place (push/splice) by addComponent/
  // removeComponent/deleteSelectedComponent/sortComponents, which classic
  // @computed's `.[]` dependent key does not observe.
  get componentOptionsForSection() {
    let allOptions;
    if (this.isHeaderSection) allOptions = this.headerComponentOptions;
    else if (this.isContentSection) allOptions = this.contentComponentOptions;
    else if (this.isDetailSection) allOptions = this.detailComponentOptions;
    else if (this.isAttendanceSection)
      allOptions = this.attendanceComponentOptions;
    else if (this.isInformationPageSection)
      allOptions = this.informationPageComponentOptions;
    else if (this.isPrincipalCommentSection)
      allOptions = this.principalCommentComponentOptions;
    else return [];

    // Filter out component types already added to the section (paragraph can be added multiple times)
    let existingComponents =
      this.get('selectedSection.configuration.components') || [];
    let usedTypes = new Set(
      existingComponents
        .map((c) => c.type || c.get('type'))
        .filter((t) => t !== 'paragraph'),
    );

    let filtered = allOptions
      .map((group) => ({
        group: group.group,
        items: group.items.filter((item) => !usedTypes.has(item.type)),
      }))
      .filter((group) => group.items.length > 0);

    return filtered;
  }

  @computed(
    'isHeaderSection',
    'isContentSection',
    'isDetailSection',
    'isAttendanceSection',
    'isInformationPageSection',
    'isPrincipalCommentSection',
  )
  get canAddComponents() {
    return (
      this.isHeaderSection ||
      this.isContentSection ||
      this.isDetailSection ||
      this.isAttendanceSection ||
      this.isInformationPageSection ||
      this.isPrincipalCommentSection
    );
  }

  @computed('selectedSection.configuration.subjectId', 'subjectOptions.[]')
  get selectedSectionSubject() {
    let subjectId = this.get('selectedSection.configuration.subjectId');
    if (!subjectId || !this.subjectOptions) return null;
    return findBy(this.subjectOptions, 'id', subjectId);
  }

  @tracked
  isSaving = false;

  @tracked rubricFamilyOptions = null;
  @tracked rubricOptions = null;

  // The families that have a rubric for the selected section's subject, with
  // the default first. Rubrics saved before families existed count as part
  // of the default family.
  @computed(
    'selectedSectionSubject.templateId',
    'rubricFamilyOptions.[]',
    'rubricOptions.[]',
  )
  get selectedSectionRubricFamilies() {
    let templateSubjectId = this.selectedSectionSubject?.templateId;
    let families = this.rubricFamilyOptions || [];
    if (!templateSubjectId || !families.length) return [];
    let defaultFamilyId = families.find((family) => family.isDefault)?.id;
    let familyIds = new Set(
      (this.rubricOptions || [])
        .filter((r) => !r.next && r.templateSubjectId === templateSubjectId)
        .map((r) => rubricFamilyIdOf(r, defaultFamilyId)),
    );
    return sortRubricFamilies(
      families.filter((family) => familyIds.has(family.id)),
    );
  }

  // Show the picker whenever a family other than the default has a rubric
  // for the subject, even if it's the only one available
  @computed('selectedSectionRubricFamilies.@each.isDefault')
  get hasRubricFamilyChoice() {
    return this.selectedSectionRubricFamilies.some(
      (family) => !family.isDefault,
    );
  }

  // The chosen family, or the default when the section hasn't chosen one
  @computed(
    'selectedSection.configuration.rubricFamilyId',
    'selectedSectionRubricFamilies.[]',
  )
  get selectedSectionRubricFamily() {
    return rubricFamilyOrDefault(
      this.selectedSectionRubricFamilies,
      this.get('selectedSection.configuration.rubricFamilyId'),
    );
  }

  @computed('template.next')
  get isOutdated() {
    return !!this.get('template.next');
  }

  @tracked
  studentNameSettings = null;
  @tracked
  studentDetailSettings = null;
  @tracked
  studentFieldOptions = null;
  @tracked
  sampleStudent = null;

  @tracked
  showDataTriangulation;

  @tracked
  sections;

  // selectedComponent.selectedFields stores raw peopleSchema/field IDs;
  // map them through studentFieldOptions to render labels in the picker list.
  // Plain getter (not @computed): selectedFields is a TrackedArray mutated
  // in place (push/splice) by addDetailField/removeDetailField/
  // sortDetailFields, which classic @computed's `.[]` dependent key does
  // not observe.
  get resolvedSelectedFields() {
    let ids = this.get('selectedComponent.selectedFields') || [];
    let options = this.studentFieldOptions || [];
    return ids
      .map((id) => {
        let field = findBy(options, 'id', id);
        if (!field) return null;
        return { id, label: field.get('label') };
      })
      .filter(Boolean);
  }

  // Lookup map for resolving a selected field id → field record while
  // iterating the raw selectedFields array (e.g. inside DragSortList).
  @computed('studentFieldOptions.[]')
  get studentFieldsById() {
    let map = {};
    (this.studentFieldOptions || []).forEach((field) => {
      map[field.get('id')] = field;
    });
    return map;
  }

  // Preferred/Legal for the template's student first and last name
  // dropdowns. Templates saved before the formats moved onto the template
  // show what their header's name keys chose.
  @computed(
    'template.studentFirstNameFormat',
    'studentNameSettings.firstNameKey',
  )
  get studentFirstNameFormatLabel() {
    return this._studentNameFormatLabel('firstName');
  }

  @computed('template.studentLastNameFormat', 'studentNameSettings.lastNameKey')
  get studentLastNameFormatLabel() {
    return this._studentNameFormatLabel('lastName');
  }

  _studentNameFormatLabel(part) {
    return ReportStudentNameFormat.getLabel(
      resolveNameFormat(this.template, this.studentNameSettings, part),
    );
  }

  _ensureSampleStudent() {
    if (this.sampleStudent) return;
    let student = this.schoolSchema.newPerson();

    // Set sample field values — "Aroha Nova" (preferred) / "Ariadne Novakovic" (legal)
    let sampleFields = [
      ['core:firstNamePreferred', 'Aroha'],
      ['core:firstNameLegal', 'Ariadne'],
      ['core:lastNamePreferred', 'Nova'],
      ['core:lastNameLegal', 'Novakovic'],
      ['core:dob', '2015-08-12'],
    ];
    sampleFields.forEach(([key, value]) => {
      student.addField(key, DataType.STRING);
      student.get(`fields.${key}`).set('value', value);
    });

    // Year level resolves from a jurisdiction-specific field
    // (nzMOE:currentYearLevel on NZ schools, core:currentYearLevel elsewhere),
    // so the sample must populate the right key or the header shows a bare
    // "Year " with no number. It must also be an INTEGER: option-list schools
    // (e.g. NSW) resolve the label via findBy('valueInt', value), which a
    // string value never matches — leaving the header showing just "4".
    let yearLevelKey =
      student.get('jurisdiction') === Jurisdiction.NZ_MOE
        ? 'nzMOE:currentYearLevel'
        : 'core:currentYearLevel';
    student.addField(yearLevelKey, DataType.INTEGER);
    student.get(`fields.${yearLevelKey}`).set('value', 4);

    this.sampleStudent = student;
  }

  _serializeComponent(comp) {
    let keys = [
      'label',
      'type',
      'icon',
      'hasSettings',
      'locked',
      'content',
      'customLabel',
      'narrativeGroup',
      'firstNameKey',
      'lastNameKey',
      'includeClass',
      'includeYearLevel',
      'includeClassTeachers',
      'classFieldKey',
      'yearLevelFieldKey',
      'chartType',
      'visualType',
      'chipLabel',
      'useSchoolColour',
      'displayType',
      'gradeScope',
    ];
    let obj = {};
    keys.forEach((key) => {
      let value = comp.get ? comp.get(key) : comp[key];
      if (value !== undefined) {
        obj[key] = value;
      }
    });
    // selectedFields is already an array of IDs
    let selectedFields = comp.get
      ? comp.get('selectedFields')
      : comp.selectedFields;
    if (selectedFields) {
      obj.selectedFields = selectedFields.toArray
        ? selectedFields.toArray()
        : [...selectedFields];
    }
    // Serialize modules as IDs only
    let modules = comp.get ? comp.get('modules') : comp.modules;
    if (modules) {
      obj.modules = modules.map((m) => {
        if (typeof m === 'string') return m;
        return m.get ? m.get('id') : m.id;
      });
    }
    return obj;
  }

  async _performSave() {
    this.isSaving = true;

    // Stash view selection before serialization mutates section configurations
    let pendingRestore = {
      sectionSequence: this.selectedSection
        ? this.selectedSection.get('sequence')
        : null,
      componentIndex:
        this.selectedComponent && this.selectedSection
          ? this.selectedSection
              .get('configuration')
              .components.indexOf(this.selectedComponent)
          : null,
    };

    try {
      if (this.template.get('isNew')) {
        // First save — standard ember-data save so the response populates
        // this.template.id; sections can then reference it via belongsTo.
        await this.template.save();

        await Promise.all(
          [...this.sections].map((section) => {
            let liveConfig = section.get('configuration');
            section.set(
              'configuration',
              JSON.stringify(this._serializeConfiguration(liveConfig)),
            );
            section.set('reportTemplateId', this.template);
            return section
              .save()
              .then(() => section.set('configuration', liveConfig));
          }),
        );

        this._resetSelectionState();
        this._pendingSelectionRestore = pendingRestore;
        this.router.replaceWith(
          'customise.report-builder-edit',
          this.template.get('id'),
        );
        return;
      }

      // Subsequent save — raw fetch so the response doesn't clobber local
      // state, and so we can detect when the server forks a new version.
      let adapter = this.store.adapterFor('reports/report-template');
      let serializer = this.store.serializerFor('reports/report-template');
      let snapshot = this.template._createSnapshot();
      let serialized = serializer.serialize(snapshot);
      delete serialized.id;
      let url = adapter.buildURL(
        'reports/report-template',
        this.template.get('id'),
        snapshot,
        'updateRecord',
      );
      // this.fetch.put() (not raw fetchWithAuthentication) so a trusted
      // response still runs handleResponse() and clears the 401/reauth
      // guard counters; it returns parsed JSON, not the store, so it can't
      // clobber local state.
      let json = await this.fetch.put(url, {
        data: JSON.stringify({ reportTemplate: serialized }),
        headers: { 'Content-Type': 'application/json' },
      });
      let payload = json.reportTemplate || json;
      let newVersionId = payload.next || null;

      // The server always forks a new version on edit. If we don't have a
      // new version id, something has changed in the API contract and we
      // should not try to recover — bail loudly so the catch toasts.
      if (!newVersionId) {
        throw new Error(
          'Server response did not include a new template version id.',
        );
      }

      // Push the updated old template into the store so its `next`
      // property is set — this ensures template list filters exclude it
      this.store.pushPayload('reports/report-template', json);

      // Fetch the new template record
      let newTemplate = await this.store.findRecord(
        'reports/report-template',
        newVersionId,
      );
      let sectionAdapter = this.store.adapterFor(
        'reports/report-template-section',
      );
      let sectionSerializer = this.store.serializerFor(
        'reports/report-template-section',
      );
      await Promise.all(
        [...this.sections].map(async (section) => {
          let liveConfig = section.get('configuration');
          let serializedConfig = JSON.stringify(
            this._serializeConfiguration(liveConfig),
          );

          if (section.get('isNew')) {
            // Brand new section — create with the new template ID
            let newSection = this.store.createRecord(
              'reports/report-template-section',
              {
                reportTemplateId: newTemplate,
                label: section.get('label'),
                icon: section.get('icon'),
                sectionType: section.get('sectionType'),
                sequence: section.get('sequence'),
                // A belongsTo takes a record, not the async proxy.
                schoolId: await section.get('schoolId'),
                configuration: serializedConfig,
              },
            );
            return newSection.save();
          }
          // Existing section — update it; API creates a new version
          // with the new reportTemplateId, leaving the old one intact
          // Send it with fetch, not the store: the API returns the old
          // section, which still points to the old template, and the store
          // does not accept a different saved belongsTo (HE-312). The
          // builder loads the new version on replaceWith.
          let sectionSnapshot = section._createSnapshot();
          let sectionJson = sectionSerializer.serialize(sectionSnapshot);
          delete sectionJson.id;
          sectionJson.configuration = serializedConfig;
          sectionJson.reportTemplateId = newVersionId;
          return this.fetch.put(
            sectionAdapter.buildURL(
              'reports/report-template-section',
              section.get('id'),
              sectionSnapshot,
              'updateRecord',
            ),
            {
              data: JSON.stringify({ reportTemplateSection: sectionJson }),
              headers: { 'Content-Type': 'application/json' },
            },
          );
        }),
      );
      // Deleted sections stay on the old version — nothing to do
      this._deletedSections = [];

      this._resetSelectionState();
      this._pendingSelectionRestore = pendingRestore;
      this.router.replaceWith('customise.report-builder-edit', newVersionId);
    } catch (e) {
      console.error('Failed to save report template:', e);
      this.toaster.error('Failed to save report template. Please try again.');
    } finally {
      this.isSaving = false;
    }
  }

  _takeSnapshot() {
    let template = this.template;
    if (!template) return null;
    let data = {
      name: template.get('name'),
      description: template.get('description'),
      theme: template.get('theme'),
      studentFirstNameFormat: template.get('studentFirstNameFormat'),
      studentLastNameFormat: template.get('studentLastNameFormat'),
      judgementWording: Object.fromEntries(
        DEFAULT_JUDGEMENT_WORDING_KEYS.map((key) => [
          key,
          template.get(`judgementWording.${key}`) || '',
        ]),
      ),
      isActive: template.get('isActive'),
      periods: (template.get('periods') || []).map((p) => ({
        label: p.get('label'),
        month: p.get('month'),
        day: p.get('day'),
      })),
      sections: (this.sections || []).map((s) => ({
        label: s.get('label'),
        icon: s.get('icon'),
        sectionType: s.get('sectionType'),
        sequence: s.get('sequence'),
        configuration: JSON.stringify(
          this._serializeConfiguration(s.get('configuration')),
        ),
      })),
    };
    return JSON.stringify(data);
  }

  _getCurrentSnapshot() {
    return this._takeSnapshot();
  }

  _serializeConfiguration(config) {
    if (!config) return {};
    let result = {};
    if (config.subjectId != null) {
      result.subjectId = config.subjectId;
    }
    if (config.rubricFamilyId != null) {
      result.rubricFamilyId = config.rubricFamilyId;
    }
    if (
      config.triangulationSubjectIds &&
      config.triangulationSubjectIds.length
    ) {
      result.triangulationSubjectIds = config.triangulationSubjectIds.toArray
        ? config.triangulationSubjectIds.toArray()
        : [...config.triangulationSubjectIds];
    }
    if (config.triangulationPageIds && config.triangulationPageIds.length) {
      result.triangulationPageIds = config.triangulationPageIds.toArray
        ? config.triangulationPageIds.toArray()
        : [...config.triangulationPageIds];
    }
    if (
      config.triangulationAssessmentIds &&
      config.triangulationAssessmentIds.length
    ) {
      result.triangulationAssessmentIds = config.triangulationAssessmentIds
        .toArray
        ? config.triangulationAssessmentIds.toArray()
        : [...config.triangulationAssessmentIds];
    }
    if (config.components) {
      result.components = config.components.map((comp) =>
        this._serializeComponent(comp),
      );
    }
    return result;
  }

  _loadStudentFieldOptions() {
    if (this.studentFieldOptions) {
      return;
    }
    this._ensureSampleStudent();
    this.schoolSchema
      .getPeopleSchema()
      .then((groups) => {
        let fields = [];
        groups.forEach((group) => {
          group.get('fields').forEach((field) => {
            if (field.get('personType') === 1) {
              // Learner
              fields.push(field);
            }
          });
        });
        fields.sort((a, b) =>
          (a.get('label') || '').localeCompare(b.get('label') || ''),
        );
        this.studentFieldOptions = fields;
      })
      .catch(() => {
        this.toaster.error('Failed to load student fields.');
      });
  }

  @action
  setComponentProperty(property, value) {
    this.set('selectedComponent.' + property, value);
    this._markDirty();
  }

  @action
  async save() {
    this.isSaving = true;

    if (this.template.get('isNew') || this.unsavedCopy) {
      // First-time save (new template, or a not-yet-saved copy of a global
      // template) — ask whether to make it active (available to teachers)
      // before persisting it. Consume the copy flag so the post-save fork
      // doesn't prompt again.
      this.unsavedCopy = false;
      this.showActivatePrompt = true;
      return;
    }

    // Detect a previously-active template being set to inactive, so we can
    // warn that its reports can no longer be created or worked with.
    let changedActive = this.template.changedAttributes().isActive;
    let deactivating =
      !!changedActive &&
      changedActive[0] === true &&
      changedActive[1] === false;

    try {
      let templateId = this.template.get('id');
      let studentReports = await this.store.query('reports/student-report', {
        reportTemplateId: templateId,
        schoolId: this.session.activeSchool,
      });

      // READY_TO_PUBLISH and above (Published, Void, Not Applicable) are
      // unaffected by a template change — RTP reports are already locked
      // in for publishing. Only pre-RTP states need to warn the user.
      let unaffectedThreshold = StudentReportState.READY_TO_PUBLISH;
      let counts = [];
      let hasAffected = false;

      StudentReportState.values.forEach((entry) => {
        if (entry.value === 0) return; // skip Unspecified
        let count = studentReports.filter(
          (report) => report.state === entry.value,
        ).length;
        if (count > 0) {
          counts.push({
            label: entry.label,
            count,
            affected: entry.value < unaffectedThreshold,
          });
          if (entry.value < unaffectedThreshold) {
            hasAffected = true;
          }
        }
      });

      if (hasAffected || deactivating) {
        this.saveConfirmationCounts = counts;
        this.saveConfirmationDeactivating = deactivating;
        this.showSaveConfirmation = true;
        return;
      }
    } catch (e) {
      console.error('Failed to check existing reports:', e);
    }

    this._performSave();
  }

  @action
  confirmSave() {
    this.showSaveConfirmation = false;
    this.saveConfirmationCounts = null;
    this.saveConfirmationDeactivating = false;
    this._performSave();
  }

  @action
  cancelSave() {
    this.showSaveConfirmation = false;
    this.saveConfirmationCounts = null;
    this.saveConfirmationDeactivating = false;
    this.isSaving = false;
  }

  @action
  activateAndSave() {
    this.showActivatePrompt = false;
    this.template.set('isActive', true);
    this._performSave();
  }

  @action
  saveWithoutActivating() {
    this.showActivatePrompt = false;
    this._performSave();
  }

  @action
  cancelActivatePrompt() {
    this.showActivatePrompt = false;
    this.isSaving = false;
  }

  @action
  updateName(value) {
    this.template.set('name', value);
    this._markDirty();
  }

  @action
  updateDescription(value) {
    this.template.set('description', value);
    this._markDirty();
  }

  // key is one of judgementWordingFields' keys. A blank box, or one left
  // on the default wording, is saved blank so the template keeps
  // following the default.
  @action
  updateJudgementWording(key, value) {
    set(this.judgementWordingValues, key, value);
    if (!this.template.judgementWording) {
      this.template.set('judgementWording', {});
    }
    let trimmed = (value || '').trim();
    this.template.set(
      `judgementWording.${key}`,
      !trimmed || trimmed === DEFAULT_JUDGEMENT_WORDING[key] ? '' : value,
    );
    this._markDirty();
  }

  // formatKey is studentFirstNameFormat or studentLastNameFormat.
  @action
  selectStudentNameFormat(formatKey, label) {
    this.template.set(
      formatKey,
      label === 'Legal'
        ? ReportStudentNameFormat.LEGAL
        : ReportStudentNameFormat.PREFERRED,
    );
    this._markDirty();
  }

  @action
  updatePeriodLabel(period, value) {
    period.set('label', value);
    this._markDirty();
  }

  // The structural actions (month, day, add, remove) re-check
  // periodsEditingDisabled rather than relying on the disabled attributes
  // alone, so a stale render can't let a period change through on a template
  // that has reports.
  @action
  updatePeriodMonth(period, option) {
    if (this.periodsEditingDisabled) return;
    period.set('month', option.value);
    // Moving to a shorter month can strand the day past the end of it
    // (31 January → February), so pull it back to the last valid day.
    let lastDay = daysInMonth(option.value);
    if (period.get('day') > lastDay) {
      period.set('day', lastDay);
    }
    this._markDirty();
  }

  // Day is locked with the rest: reports from Ready to Publish onwards stay
  // on the version they were approved against, so a moved end date leaves
  // two versions disagreeing on when the period ends and splits its cards.
  @action
  updatePeriodDay(period, option) {
    if (this.periodsEditingDisabled) return;
    period.set('day', option.value);
    this._markDirty();
  }

  @action
  addPeriod() {
    if (this.periodsEditingDisabled) return;
    this.template.get('periods').createFragment({
      label: '',
      month: 1,
      day: 28,
    });
    this._markDirty();
  }

  @action
  removePeriod(period) {
    if (this.periodsEditingDisabled) return;
    let periods = this.template.get('periods');
    if (periods.get('length') <= 1) {
      this.toaster.error('A report template must have at least one period.');
      return;
    }
    periods.removeFragment(period);
    this._markDirty();
  }

  @action
  selectTheme(themeLabel) {
    let entry = ReportTemplateTheme.values.find((v) => v.label === themeLabel);
    this.template.set('theme', entry ? entry.value : ReportTemplateTheme.TEAL);
    this._markDirty();
  }

  @action
  selectSection(section) {
    this.selectedSection = section;
    this.showComponentPicker = false;
    if (section.get('sectionType') === ReportTemplateSectionType.CONTENT) {
      this._loadSubjects();
    }
  }

  @action
  openDataTriangulation() {
    this.selectedComponent = null;
    this.showComponentPicker = false;
    this.showDataTriangulation = true;
    this._loadSubjects();
    this._loadPages();
    this._loadAssessments();
  }

  @action
  closeDataTriangulation() {
    this.showDataTriangulation = false;
  }

  @action
  addTriangulationSubjects() {
    this._loadSubjects();
    let existing =
      this.get('selectedSection.configuration.triangulationSubjectIds') || [];
    this.pendingTriangulationSubjects = [...existing];
    this.showTriangulationSubjectPicker = true;
  }

  @action
  toggleTriangulationSubject(subject) {
    let pending = this.pendingTriangulationSubjects;
    let id = subject.get('id');
    this.pendingTriangulationSubjects = pending.includes(id)
      ? pending.filter((existingId) => existingId !== id)
      : [...pending, id];
  }

  @action
  confirmTriangulationSubjects() {
    this.selectedSection.set('configuration.triangulationSubjectIds', [
      ...this.pendingTriangulationSubjects,
    ]);
    this.notifyPropertyChange('selectedSection.configuration');
    this.showTriangulationSubjectPicker = false;
    this.pendingTriangulationSubjects = null;
    this._markDirty();
  }

  @action
  cancelTriangulationSubjects() {
    this.showTriangulationSubjectPicker = false;
    this.pendingTriangulationSubjects = null;
  }

  @action
  removeTriangulationSubject(subjectId) {
    let ids = this.get('selectedSection.configuration.triangulationSubjectIds');
    if (ids) {
      this.selectedSection.set(
        'configuration.triangulationSubjectIds',
        ids.filter((id) => id !== subjectId),
      );
      this.selectedSection.notifyPropertyChange('configuration');
    }
    this._markDirty();
  }

  @action
  addTriangulationPages() {
    this._loadPages();
    let existing =
      this.get('selectedSection.configuration.triangulationPageIds') || [];
    this.pendingTriangulationPages = [...existing];
    this.showTriangulationPagePicker = true;
  }

  @action
  toggleTriangulationPage(page) {
    let pending = this.pendingTriangulationPages;
    let id = page.get('id');
    this.pendingTriangulationPages = pending.includes(id)
      ? pending.filter((existingId) => existingId !== id)
      : [...pending, id];
  }

  @action
  confirmTriangulationPages() {
    this.selectedSection.set('configuration.triangulationPageIds', [
      ...this.pendingTriangulationPages,
    ]);
    this.selectedSection.notifyPropertyChange('configuration');
    this.showTriangulationPagePicker = false;
    this.pendingTriangulationPages = null;
    this._markDirty();
  }

  @action
  cancelTriangulationPages() {
    this.showTriangulationPagePicker = false;
    this.pendingTriangulationPages = null;
  }

  @action
  removeTriangulationPage(pageId) {
    let ids = this.get('selectedSection.configuration.triangulationPageIds');
    if (ids) {
      this.selectedSection.set(
        'configuration.triangulationPageIds',
        ids.filter((id) => id !== pageId),
      );
      this.selectedSection.notifyPropertyChange('configuration');
    }
    this._markDirty();
  }

  @action
  addTriangulationAssessments() {
    this._loadAssessments();
    let existing =
      this.get('selectedSection.configuration.triangulationAssessmentIds') ||
      [];
    this.pendingTriangulationAssessments = [...existing];
    this.showTriangulationAssessmentPicker = true;
  }

  @action
  toggleTriangulationAssessment(assessment) {
    let pending = this.pendingTriangulationAssessments;
    let id = assessment.get('id');
    this.pendingTriangulationAssessments = pending.includes(id)
      ? pending.filter((existingId) => existingId !== id)
      : [...pending, id];
  }

  @action
  confirmTriangulationAssessments() {
    this.selectedSection.set('configuration.triangulationAssessmentIds', [
      ...this.pendingTriangulationAssessments,
    ]);
    this.selectedSection.notifyPropertyChange('configuration');
    this.showTriangulationAssessmentPicker = false;
    this.pendingTriangulationAssessments = null;
    this._markDirty();
  }

  @action
  cancelTriangulationAssessments() {
    this.showTriangulationAssessmentPicker = false;
    this.pendingTriangulationAssessments = null;
  }

  @action
  removeTriangulationAssessment(assessmentId) {
    let ids = this.get(
      'selectedSection.configuration.triangulationAssessmentIds',
    );
    if (ids) {
      this.selectedSection.set(
        'configuration.triangulationAssessmentIds',
        ids.filter((id) => id !== assessmentId),
      );
      this.selectedSection.notifyPropertyChange('configuration');
    }
    this._markDirty();
  }

  @action
  updateSectionTitle(value) {
    this.set('selectedComponent.content', value);
    this.selectedSection.set('label', value);
    this._markDirty();
  }

  // Choosing the default family stores no id, so the section follows
  // whichever family is the default.
  @action
  selectRubricFamily(family) {
    this.selectedSection.set(
      'configuration.rubricFamilyId',
      family && !family.isDefault ? family.id : null,
    );
    this._markDirty();
  }

  @action
  selectSubject(subject) {
    this.selectedSection.set(
      'configuration.subjectId',
      subject ? subject.get('id') : null,
    );
    if (subject) {
      let label = subject.get('label');
      this.selectedSection.set('label', label);
      let titleComp = findBy(
        this.selectedSection.get('configuration.components') || [],
        'type',
        'section-title',
      );
      if (titleComp) {
        titleComp.set('content', label);
      }
    }
    this._markDirty();
  }

  @action
  backToOverview() {
    this.selectedSection = null;
    this.showComponentPicker = false;
  }

  @action
  removeSection(section) {
    let idx = this.sections.indexOf(section);
    if (idx > -1) {
      this.sections.splice(idx, 1);
    }
    if (this.selectedSection === section) {
      this.selectedSection = null;
    }
    if (section.get('isNew')) {
      section.deleteRecord();
    } else {
      // Mark the existing record as deleted so the live preview's
      // peekAll-based filter excludes it. Server-side deletion isn't
      // needed — the post-save fork just doesn't carry it forward —
      // but the local flag is what hides it from ReportView.
      section.deleteRecord();
      this._deletedSections.push(section);
    }
    this._markDirty();
  }

  @action
  toggleActive() {
    this.template.set('isActive', !this.template.get('isActive'));
    this._markDirty();
  }

  @action
  toggleTemplateAttributes() {
    this.toggleProperty('showTemplateAttributes');
  }

  // From the preview's period badge. The attributes card lives in the
  // default form pane, so leave any drill-in first, then scroll once the
  // pane has re-rendered with the card open.
  @action
  openPeriodsSetup() {
    this._clearViewSelection();
    this.showDataTriangulation = false;
    this.showComponentPicker = false;
    this.showTemplateAttributes = true;
    scheduleOnce('afterRender', this, this._scrollToPeriods);
  }

  // Dismisses the drill-in outright, from whichever level it is at. The back
  // links step up one level; the mobile sheet's close button drops straight
  // back to the template overview.
  @action
  closeDetail() {
    this.selectedComponent = null;
    this.selectedSection = null;
    this.showDataTriangulation = false;
    this.showComponentPicker = false;
  }

  @action
  toggleSectionTypes() {
    this.toggleProperty('showSectionTypes');
  }

  @action
  addSectionOfType(typeOption) {
    let section = this.store.createRecord('reports/report-template-section', {
      reportTemplateId: this.template,
      label: typeOption.label,
      icon: typeOption.icon,
      sectionType: typeOption.sectionType,
      sequence: this.sections.length,
      schoolId: this.session.school,
    });
    // Initialise configuration with components array
    let components = new TrackedArray();
    if (typeOption.sectionType === ReportTemplateSectionType.HEADER) {
      this._ensureSampleStudent();
      let studentNameComponent = EmberObject.create({
        label: 'Student name and avatar',
        type: 'student-name-avatar',
        icon: 'person',
        locked: true,
        hasSettings: true,
        includeClass: true,
        includeYearLevel: true,
        includeClassTeachers: false,
        classFieldKey: 'learner:homeroom',
        yearLevelFieldKey: 'core:currentYearLevel',
      });
      components.push(
        {
          label: 'School name and logo',
          type: 'school-name-logo',
          icon: 'school',
          locked: true,
        },
        studentNameComponent,
      );
      this.studentNameSettings = studentNameComponent;
    }
    let configuration = { components };
    if (typeOption.sectionType === ReportTemplateSectionType.CONTENT) {
      this._loadSubjects();
      configuration.subjectId = null;
      configuration.triangulationSubjectIds = [];
      configuration.triangulationPageIds = [];
      configuration.triangulationAssessmentIds = [];
      components.push(
        EmberObject.create({
          label: 'Section title',
          type: 'section-title',
          icon: 'title',
          locked: true,
          hasSettings: true,
          content: 'Content',
        }),
      );
    }
    if (typeOption.sectionType === ReportTemplateSectionType.DETAIL) {
      components.push(
        EmberObject.create({
          label: 'Section title',
          type: 'section-title',
          icon: 'title',
          locked: true,
          hasSettings: true,
          content: 'Details',
        }),
      );
    }
    if (typeOption.sectionType === ReportTemplateSectionType.ATTENDANCE) {
      components.push(
        EmberObject.create({
          label: 'Section title',
          type: 'section-title',
          icon: 'title',
          locked: true,
          hasSettings: true,
          content: 'Attendance',
        }),
        EmberObject.create({
          label: 'Attendance Data',
          type: 'attendance-data',
          icon: 'event_available',
          hasSettings: true,
          displayType: 'chart',
        }),
      );
    }
    if (typeOption.sectionType === ReportTemplateSectionType.INFORMATION_PAGE) {
      components.push(
        EmberObject.create({
          label: 'Section title',
          type: 'section-title',
          icon: 'title',
          locked: true,
          hasSettings: true,
          content: 'Information',
        }),
        EmberObject.create({
          label: 'Paragraph',
          type: 'paragraph',
          icon: 'text_fields',
          hasSettings: true,
          content: '',
        }),
      );
    }
    if (
      typeOption.sectionType === ReportTemplateSectionType.PRINCIPAL_COMMENT
    ) {
      components.push(
        EmberObject.create({
          label: 'Section title',
          type: 'section-title',
          icon: 'title',
          locked: true,
          hasSettings: true,
          content: 'Leadership Comment',
        }),
        EmberObject.create({
          label: 'Paragraph',
          type: 'paragraph',
          icon: 'text_fields',
          hasSettings: true,
          content: '',
        }),
        EmberObject.create({
          label: 'Comment',
          type: 'principal-comment',
          icon: 'comment',
          hasSettings: true,
          narrativeGroup: true,
          content: '',
        }),
      );
    }
    section.set('configuration', configuration);
    if (typeOption.sectionType === ReportTemplateSectionType.HEADER) {
      this.sections.splice(0, 0, section);
    } else {
      this.sections.push(section);
    }
    this.showSectionTypes = false;
    this._markDirty();
  }

  @action
  sortSections({ sourceList, sourceIndex, targetList, targetIndex }) {
    if (sourceList === targetList && sourceIndex === targetIndex) {
      return;
    }
    const item = sourceList[sourceIndex];
    sourceList.splice(sourceIndex, 1);
    targetList.splice(targetIndex, 0, item);
    // Snap header back to top
    let headerIdx = this.sections.findIndex(
      (s) => s.get('sectionType') === ReportTemplateSectionType.HEADER,
    );
    if (headerIdx > 0) {
      let header = this.sections[headerIdx];
      this.sections.splice(headerIdx, 1);
      this.sections.splice(0, 0, header);
    }
    // Update sequence numbers
    this.sections.forEach((s, i) => s.set('sequence', i));
    this._markDirty();
  }

  @action
  toggleComponentPicker() {
    this.toggleProperty('showComponentPicker');
  }

  @action
  closeComponentPicker() {
    this.showComponentPicker = false;
  }

  @action
  addComponent(componentOption) {
    let component = EmberObject.create({
      label: componentOption.label,
      type: componentOption.type,
      icon: componentOption.icon,
      hasSettings: componentOption.hasSettings || false,
      narrativeGroup: componentOption.narrativeGroup || false,
      content: '',
    });
    // Add default settings for Student name and avatar
    if (component.get('type') === 'student-name-avatar') {
      this._ensureSampleStudent();
      component.setProperties({
        includeClass: true,
        includeYearLevel: true,
        includeClassTeachers: false,
        classFieldKey: 'learner:homeroom',
        yearLevelFieldKey: 'core:currentYearLevel',
      });
      this.studentNameSettings = component;
    }
    // Add defaults from component option
    if (componentOption.defaultChartType) {
      component.set('chartType', componentOption.defaultChartType);
    }
    if (componentOption.defaultChipLabel != null) {
      component.set('chipLabel', componentOption.defaultChipLabel);
      component.set(
        'useSchoolColour',
        componentOption.defaultUseSchoolColour !== false,
      );
    }
    if (componentOption.defaultVisualType) {
      component.set('visualType', componentOption.defaultVisualType);
    }
    // Add default settings for Assessment Data
    if (component.get('type') === 'assessment-data') {
      component.setProperties({
        displayType: 'table',
        modules: [],
        gradeScope: 'current-year',
      });
    }
    // Add default settings for Student detail
    if (component.get('type') === 'student-detail') {
      component.set('selectedFields', new TrackedArray());
      this.studentDetailSettings = component;
      this._loadStudentFieldOptions();
    }
    this.selectedSection.get('configuration').components.push(component);
    this.showComponentPicker = false;
    if (component.hasSettings) {
      this.selectedComponent = component;
    }
    this._markDirty();
  }

  // The report preview calls @onSelectComponent(section, component).
  @action
  selectPreviewComponent(section, component) {
    this.selectSection(section);
    this.selectComponent(component);
  }

  // The section panel calls `(fn this.selectComponent item)` from `{{on}}`,
  // which adds the click event as a second argument. Ignore it.
  @action
  selectComponent(component) {
    if (component.hasSettings) {
      if (component.type === 'student-detail') {
        this._loadStudentFieldOptions();
      }
      this.selectedComponent = component;
    }
  }

  @action
  addDetailField(field) {
    if (!field) {
      return;
    }
    let component = this.selectedComponent;
    let fieldId = field.get('id');
    if (!component.get('selectedFields').includes(fieldId)) {
      component.get('selectedFields').push(fieldId);
    }
    this._markDirty();
  }

  @action
  removeDetailField(fieldId) {
    let selectedFields = this.selectedComponent.get('selectedFields');
    let idx = selectedFields.indexOf(fieldId);
    if (idx > -1) {
      selectedFields.splice(idx, 1);
    }
    this._markDirty();
  }

  @action
  backToSection() {
    this.selectedComponent = null;
  }

  @action
  deleteSelectedComponent() {
    let component = this.selectedComponent;
    let components = this.selectedSection.get('configuration').components;
    let idx = components.indexOf(component);
    if (idx > -1) {
      components.splice(idx, 1);
    }
    this.selectedComponent = null;
    this._markDirty();
  }

  @action
  toggleModulePicker() {
    this._loadAssessments();
    this._loadModules();
    let existing = this.selectedComponent.get('modules') || [];
    this.pendingModuleSelections = existing.map((m) => m.id || m.get('id'));
    this.modulePickerSearch = '';
    this.showModulePicker = true;
  }

  @action
  closeModulePicker() {
    this.showModulePicker = false;
  }

  @action
  toggleModuleSelection(mod) {
    let id = mod.get('id');
    let pending = this.pendingModuleSelections;
    this.pendingModuleSelections = pending.includes(id)
      ? pending.filter((existingId) => existingId !== id)
      : [...pending, id];
  }

  @action
  confirmModuleSelection() {
    let modules = this.moduleOptions;
    let selected = this.pendingModuleSelections;
    let result = selected
      .map((id) => {
        let mod = findBy(modules, 'id', id);
        if (!mod) return null;
        return { id: mod.get('id'), label: mod.get('label') };
      })
      .filter(Boolean);
    this.selectedComponent.set('modules', result);
    this.showModulePicker = false;
    this.selectedSection.notifyPropertyChange('configuration');
    this._markDirty();
  }

  @action
  removeAssessmentModule(mod) {
    let modules = this.selectedComponent.get('modules');
    this.selectedComponent.set(
      'modules',
      modules.filter((m) => m !== mod),
    );
    this._markDirty();
  }

  @action
  removeComponent(component) {
    let components = this.selectedSection.get('configuration').components;
    let idx = components.indexOf(component);
    if (idx > -1) {
      components.splice(idx, 1);
    }
    if (this.selectedComponent === component) {
      this.selectedComponent = null;
    }
    this._markDirty();
  }

  @action
  sortComponents({ sourceList, sourceIndex, targetList, targetIndex }) {
    if (sourceList === targetList && sourceIndex === targetIndex) {
      return;
    }
    const item = sourceList[sourceIndex];
    sourceList.splice(sourceIndex, 1);
    targetList.splice(targetIndex, 0, item);
    this._markDirty();
  }

  @action
  sortDetailFields({ sourceList, sourceIndex, targetList, targetIndex }) {
    if (sourceList === targetList && sourceIndex === targetIndex) {
      return;
    }
    const item = sourceList[sourceIndex];
    sourceList.splice(sourceIndex, 1);
    targetList.splice(targetIndex, 0, item);
    this._markDirty();
  }
}
